
import React from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Trophy, Share2, Zap, Sparkles, Crown, Eye, EyeOff, Info } from "lucide-react";
import { isGameTypeAvailable, type GameType, type SubscriptionPlan } from "@/utils/predictionUtils";
import PredictionGrid from "./PredictionGrid";

interface PredictionContentProps {
  gameType: GameType;
  title: string;
  predictions: string[];
  userPlan: SubscriptionPlan;
  isFederalLottery: boolean;
  isGenerating: boolean;
  selectedState: string;
  selectedAI: "gemini" | "chatgpt" | "claude" | "deepseek";
  selectedLottery: string;
  gameCount: number;
  showLastUpdate: boolean;
  setShowLastUpdate: (show: boolean) => void;
  onGeneratePredictions: (gameType: GameType) => void;
  onShare: (gameType: GameType, title: string) => void;
  getLotteryName: (lotteryValue: string) => string;
}

const PredictionContent: React.FC<PredictionContentProps> = ({
  gameType,
  title,
  predictions,
  userPlan,
  isFederalLottery,
  isGenerating,
  selectedState,
  selectedAI,
  selectedLottery,
  gameCount,
  showLastUpdate,
  setShowLastUpdate,
  onGeneratePredictions,
  onShare,
  getLotteryName
}) => {
  const navigate = useNavigate();
  const isAvailable = isGameTypeAvailable(userPlan, gameType);
  
  if (!isAvailable && userPlan === "free") {
    return (
      <div className="text-center space-y-6 py-12">
        <div className={`rounded-lg p-8 border ${
          isFederalLottery 
            ? 'bg-gradient-to-br from-gold-900/30 to-yellow-900/20 border-gold-400/50' 
            : 'bg-gradient-to-br from-yellow-50/50 to-orange-50/50 dark:from-yellow-950/20 dark:to-orange-950/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <Trophy className={`h-12 w-12 mx-auto mb-4 ${isFederalLottery ? 'text-gold-400' : 'text-yellow-500'}`} />
          <p className={`text-lg font-semibold mb-2 ${
            isFederalLottery ? 'text-gold-200' : 'text-gray-800 dark:text-gray-200'
          }`}>
            Funcionalidade Premium
          </p>
          <p className={`mb-4 ${
            isFederalLottery ? 'text-gold-300' : 'text-muted-foreground'
          }`}>
            Upgrade seu plano para acessar {title.toLowerCase()} com IA
          </p>
          <Button 
            onClick={() => navigate("/planos")}
            className="btn-gradient px-6 py-2"
            size="lg"
          >
            🚀 Desbloquear Agora
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {predictions.length > 0 && (
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <h3 className={`text-2xl font-bold text-center ${
              isFederalLottery ? 'text-gold-200' : ''
            }`}>
              🎯 Palpites Gerados
            </h3>
            {userPlan === "free" && (gameType === "milhar" || gameType === "centena") && (
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                <Sparkles className="w-4 h-4 inline mr-1" />
                2 de {gameCount} palpites
              </div>
            )}
          </div>

          {/* Aviso informativo sobre os palpites */}
          <div className={`rounded-lg p-4 mb-6 border ${
            isFederalLottery 
              ? 'bg-blue-900/30 border-blue-400/50' 
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-start gap-3">
              <Info className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                isFederalLottery ? 'text-blue-300' : 'text-blue-600 dark:text-blue-400'
              }`} />
              <div className={`text-sm ${
                isFederalLottery ? 'text-blue-200' : 'text-blue-700 dark:text-blue-300'
              }`}>
                <p className="font-semibold mb-1">Informações dos Palpites:</p>
                <p>
                  <strong>IA:</strong> {selectedAI === "gemini" ? "Gemini" : selectedAI === "claude" ? "Claude.ai" : selectedAI === "deepseek" ? "DeepSeek" : "ChatGPT"} • 
                  <strong> Estado:</strong> {selectedState === "todos" ? "Todos os Estados" : selectedState === "brasil" ? "Brasil" : selectedState} • 
                  <strong> Loteria:</strong> {getLotteryName(selectedLottery)}
                </p>
              </div>
            </div>
          </div>
          
          <PredictionGrid
            gameType={gameType}
            predictions={predictions}
            userPlan={userPlan}
            isFederalLottery={isFederalLottery}
          />
          
          {/* Mensagem motivacional para usuários gratuitos */}
          {userPlan === "free" && (gameType === "milhar" || gameType === "centena") && predictions.length > 0 && (
            <div className={`rounded-lg p-6 mt-6 border ${
              isFederalLottery 
                ? 'bg-gradient-to-br from-gold-900/40 to-yellow-900/30 border-gold-400/50' 
                : 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20 border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Crown className={`w-5 h-5 ${isFederalLottery ? 'text-gold-400' : 'text-yellow-600'}`} />
                <span className={`font-bold ${
                  isFederalLottery ? 'text-gold-200' : 'text-yellow-700 dark:text-yellow-300'
                }`}>
                  Você está vendo apenas 2 de {gameCount} palpites!
                </span>
              </div>
              <p className={`mb-4 text-sm ${
                isFederalLottery ? 'text-gold-300' : 'text-yellow-600 dark:text-yellow-400'
              }`}>
                Desbloqueie todos os palpites e tenha acesso completo aos recursos premium da nossa IA.
              </p>
              <Button 
                onClick={() => navigate("/planos")}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold px-6 py-2 shadow-lg"
              >
                ✨ Ver Todos os {gameCount} Palpites
              </Button>
            </div>
          )}
        </div>
      )}

      <div className={`flex flex-col sm:flex-row justify-center items-center gap-4 rounded-lg p-4 ${
        isFederalLottery ? 'bg-gray-800/40' : 'bg-muted/30'
      }`}>
        <div className="flex gap-2">
          <Button 
            onClick={() => onGeneratePredictions(gameType)}
            disabled={isGenerating || !selectedState || !selectedAI}
            className="btn-gradient"
          >
            {isGenerating ? "Gerando..." : `🎯 Gerar ${gameCount} palpites`}
          </Button>
          {predictions.length > 0 && (
            <Button
              onClick={() => onShare(gameType, title)}
              variant="outline"
              className={`flex items-center gap-2 ${
                isFederalLottery ? 'border-gold-400/50 text-gold-200 hover:bg-gold-900/20' : ''
              }`}
            >
              <Share2 className="h-4 w-4" />
              Compartilhar
            </Button>
          )}
        </div>
      </div>

      {showLastUpdate && (
        <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 rounded-lg p-4 ${
          isFederalLottery ? 'bg-gray-800/30' : 'bg-muted/20'
        }`}>
          <div className="flex items-center gap-4">
            <div className={`text-sm ${isFederalLottery ? 'text-gold-300' : 'text-muted-foreground'}`}>
              <p>📅 Última atualização: 17:32:25 - 12/06/2025</p>
              <p>🤖 IA utilizada: {selectedAI === 'gemini' ? 'GEMINI' : selectedAI === 'claude' ? 'CLAUDE.AI' : selectedAI === 'deepseek' ? 'DEEPSEEK' : 'CHATGPT'}</p>
              <p>📍 Estado: {selectedState === "todos" ? "Todos os Estados" : selectedState === "brasil" ? "Brasil" : selectedState}</p>
              <p>🎲 Loteria: {getLotteryName(selectedLottery)}</p>
              <p>🎯 Quantidade: {gameCount} jogos</p>
              <p>📊 Plano atual: {userPlan === "free" ? "Gratuito (2 palpites)" : "Premium"}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLastUpdate(!showLastUpdate)}
            className={`flex items-center gap-1 ${
              isFederalLottery ? 'text-gold-300 hover:text-gold-200' : ''
            }`}
          >
            <EyeOff className="h-4 w-4" />
            Ocultar
          </Button>
        </div>
      )}

      {!showLastUpdate && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLastUpdate(!showLastUpdate)}
            className={`flex items-center gap-1 ${
              isFederalLottery ? 'text-gold-300 hover:text-gold-200' : ''
            }`}
          >
            <Eye className="h-4 w-4" />
            Mostrar última atualização
          </Button>
        </div>
      )}

      {!selectedState && (
        <div className={`rounded-lg p-4 border ${
          isFederalLottery 
            ? 'bg-blue-900/20 border-blue-400/50 text-blue-200' 
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
        }`}>
          <p className="text-sm">
            ⚠️ Selecione uma IA e um estado para poder gerar palpites.
          </p>
        </div>
      )}

      {predictions.length === 0 && (
        <div className="text-center py-12">
          <div className={`rounded-lg p-8 border border-dashed ${
            isFederalLottery 
              ? 'bg-gray-800/30 border-gold-400/30' 
              : 'bg-muted/30 border-muted-foreground/30'
          }`}>
            <Zap className={`h-12 w-12 mx-auto mb-4 ${
              isFederalLottery ? 'text-gold-400' : 'text-muted-foreground'
            }`} />
            <p className={`text-lg font-medium mb-2 ${
              isFederalLottery ? 'text-gold-200' : 'text-muted-foreground'
            }`}>
              Pronto para gerar palpites!
            </p>
            <p className={`text-sm ${
              isFederalLottery ? 'text-gold-300' : 'text-muted-foreground'
            }`}>
              Selecione uma IA e um estado, depois clique em "Gerar palpites" para ver os resultados de {title.toLowerCase()}
            </p>
          </div>
        </div>
      )}

      {predictions.length > 0 && (
        <div className="text-center pt-4">
          <p className={`text-xs ${
            isFederalLottery ? 'text-gold-400' : 'text-muted-foreground'
          }`}>
            Os palpites são gerados com base em análises de resultados anteriores e algoritmos de IA para o estado: {selectedState === "todos" ? "Todos os Estados" : selectedState === "brasil" ? "Brasil" : selectedState}.
          </p>
        </div>
      )}
    </div>
  );
};

export default PredictionContent;
